import React, {
	LegacyRef,
	memo,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { FieldValues } from 'react-hook-form';
import cn from 'classnames';

import { isEmpty } from '@utils/common';
import { getAfterElementStyles, getBeforeElementStyles } from './helpers/getStyles';

import { TValueOfStringRecord } from '@interfaces/common/genericWithModifying';
import { ECheckboxSizes, ICheckboxProps } from './Checkbox.types';

import { predefinedColors } from '@constants/colors';

import Tooltip from '../Tooltip';

import './Checkbox.scss';

export const ECheckboxColors = {
	default: 'DEFAULT',
	red: 'RED',
	green: 'GREEN',
	amber: 'AMBER',
	darkPurple: 'DARK_PURPLE',
	darkGreen: 'DARK_GREEN',
	orange: 'ORANGE',
} as const;

export type TCheckboxColors = TValueOfStringRecord<typeof ECheckboxColors>

function Checkbox<T extends FieldValues>({
	checked = false,
	classNames,
	color,
	defaultChecked,
	disabled,
	disabledTooltip,
	isError = false,
	isFilledStyle = false,
	isIndefinite,
	isOverflow = false,
	isPropagation = false,
	isUncheckedDisabled,
	label,
	lineHeight = 20,
	maxWidthOverflow = 0,
	name,
	onChange,
	onChangeForTable,
	readOnly,
	register,
	size = ECheckboxSizes.md,
	tags,
	withUseForm = false,
	isLabelTooltipDisabled,
}: ICheckboxProps<T>) {
	const labelRef = useRef<HTMLLabelElement>(null);
	const tagsRef = useRef<HTMLLabelElement>(null);
	const [isFullSize, setIsFullSize] = useState(false);
	const [isOverflowTags, setIsOverflowTags] = useState(false);

	const withOverflowStyle = useMemo(() => isOverflow && !isFullSize, [isFullSize, isOverflow]);

	const classes = cn('f5gCheckboxContainer', 'f5gCheckbox', classNames, {
		'f5gCheckboxIndefinite': isIndefinite,
		'f5gCheckboxDisabled': isUncheckedDisabled ? disabled || !checked : disabled,
		'f5gCheckboxWithTags': !isEmpty(tags),
		[ECheckboxColors.red]: isError,
	});

	const calculateFullSize = useCallback(() => {
		if (!label?.length) return;

		if (labelRef?.current?.offsetWidth) {
			const fullSizeCheckbox = labelRef.current.offsetWidth;

			setIsFullSize(fullSizeCheckbox <= maxWidthOverflow);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [label?.length]);

	const calculateFullSizeWithTags = useCallback(() => {
		if (!label?.length) return;

		if (tagsRef?.current) {
			const tagsContainerRight = tagsRef.current.getBoundingClientRect().right;
			const lastTag = tagsRef.current.querySelector('.f5gCheckboxTag:last-child');
			const lastTagOffsetRight = (lastTag?.getBoundingClientRect().left || 0)
				+ (lastTag?.getBoundingClientRect().width || 0);

			setIsOverflowTags(tagsContainerRight < lastTagOffsetRight);
		}
	}, [label?.length]);

	useLayoutEffect(() => {
		if (!isOverflow) return;

		calculateFullSize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [label, labelRef.current, isOverflow]);

	useLayoutEffect(() => {
		calculateFullSizeWithTags();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tags, tagsRef.current, isOverflow]);

	const handleOnChange = (e: React.MouseEvent<HTMLLabelElement>) => {
		if (isPropagation) {
			e.stopPropagation();
		}

		if (readOnly || disabled) return;

		if (onChangeForTable) onChangeForTable({ target: { checked: !checked } });
		if (onChange) onChange(!checked, name);
	};

	const Wrapper = disabledTooltip ? Tooltip : React.Fragment;

	const checkboxColor = predefinedColors[color as keyof typeof predefinedColors] || color;

	const beforeElementStyle = getBeforeElementStyles(size);
	const afterElementStyle = getAfterElementStyles(size, isIndefinite);

	return (
		<Wrapper
			{...disabledTooltip ? {
				content: disabledTooltip,
				noWordBreak: true,
				placement: 'bottom',
			} : {}}
		>
			<div
				className={classes}
				data-checked={checked}
				data-name={name}
				data-testid='checkbox-container'
			>
				{withUseForm
					? <input
						aria-label={label || 'empty-label-checkbox'}
						checked={checked}
						className='f5gCheckbox'
						disabled={isUncheckedDisabled ? disabled || !checked : disabled}
						id={name}
						readOnly
						{...register ? register(name) : {}}
						data-is-indeterminate={isIndefinite}
						type='checkbox'
					/>
					: <input
						ref={register as LegacyRef<HTMLInputElement>}
						aria-label={label || 'empty-label-checkbox'}
						checked={checked}
						className='f5gCheckbox'
						data-is-indeterminate={isIndefinite}
						data-testid='checkbox-input'
						defaultChecked={defaultChecked}
						disabled={isUncheckedDisabled ? disabled || !checked : disabled}
						id={name}
						name={name}
						readOnly
						type='checkbox'
					/>
				}
				<Tooltip
					content={label}
					isDisabled={isLabelTooltipDisabled || !withOverflowStyle}
					placement='top'
				>

					<label
						ref={labelRef}
						className={cn('f5gCheckboxLabel', {
							'f5gCheckboxLabelNowrap': isOverflow,
							'f5gCheckboxLabelEllipsis': withOverflowStyle,
						})}
						data-checked={checked}
						data-testid='checkbox-label'
						htmlFor={name}
						onClick={handleOnChange}
						style={{
							lineHeight: `${lineHeight}px`,
							display: withOverflowStyle ? 'block' : 'inline',
							maxWidth: withOverflowStyle ? `${maxWidthOverflow}px` : 'none',
						}}
					>
							<span
								className='before'
								style={{
									borderColor: checkboxColor,
									backgroundColor: checked && !isIndefinite
										? checkboxColor
										: isFilledStyle ? checkboxColor : 'transparent',
									...beforeElementStyle,
								}}
							/>
						<span
							className='after'
							style={{
								backgroundColor: isIndefinite ? checkboxColor : 'transparent',
								...afterElementStyle,
							}}
						/>
						{label}
					</label>
				</Tooltip>
				{!isEmpty(tags) && (
					<Tooltip
						content={tags?.join(', ')}
						isDisabled={!isOverflowTags}
						placement='top'
					>
						<span
							ref={tagsRef}
							className='f5gCheckboxTagsContainer'
						>
							{tags?.map((tag, index) => (
								<span
									key={`${index}-${tag}`}
									className='f5gCheckboxTag'
								>
									{tag}
								</span>
							))}
						</span>
					</Tooltip>
				)}
			</div>
		</Wrapper>
	);
}

export default memo(Checkbox);
